import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col, Form, FormGroup, Label, Input } from "reactstrap";
import LottieLoader from "../LottieLoader";
import lottieLoaderFile from "../../files/animation/loading.json";
import lottieEmptyFile from "../../files/animation/empty_new.json";

const VITAL_GOAL_FIELDS = [
  {
    name: "bloodPressureGoal",
    label: "Blood Pressure Goal",
    unit: "mmHg",
    placeholder: "e.g., Average < 130/80 mmHg within 90 days"
  },
  {
    name: "glucoseGoal",
    label: "Glucose Goal",
    unit: "mg/dL",
    placeholder: "e.g., Fasting glucose 80–130 mg/dL"
  },
  {
    name: "weightGoal",
    label: "Weight Goal",
    unit: "lb",
    placeholder: "e.g., Maintain weight within 2 lb of baseline"
  },
  {
    name: "bloodOxygenGoal",
    label: "Blood Oxygen Goal",
    unit: "%",
    placeholder: "e.g., SpO2 ≥ 94% on room air"
  },
  {
    name: "temperatureGoal",
    label: "Temperature Goal",
    unit: "°F",
    placeholder: "e.g., Body temperature 97–99 °F"
  },
  {
    name: "activityGoal",
    label: "Activity Goal",
    unit: "steps",
    placeholder: "e.g., ≥ 5,000 steps per day"
  }
];

const OTHER_GOAL_FIELDS = [
  {
    name: "medicationAdherence",
    label: "Medication Adherence",
    placeholder: "e.g., ≥ 90%"
  },
  {
    name: "hospitalizations",
    label: "Hospitalizations",
    placeholder: "e.g., Zero preventable admissions"
  }
];

const ALL_GOAL_FIELDS = [...VITAL_GOAL_FIELDS, ...OTHER_GOAL_FIELDS];

const DEFAULT_FIELDS = ALL_GOAL_FIELDS.reduce((acc, field) => {
  acc[field.name] = "";
  return acc;
}, {});

function buildDefaultForm(goal) {
  return ALL_GOAL_FIELDS.reduce((acc, field) => {
    acc[field.name] = goal ? goal[field.name] || "" : "";
    return acc;
  }, {});
}

function GoalField({ field, value, onChange, editing }) {
  const unitHint = field.unit ? ` (${field.unit})` : "";
  return (
    <FormGroup>
      <Label className={editing ? "" : "font-weight-bold"}>
        {editing ? <strong>{field.label}</strong> : field.label}
        {editing && <span className="text-muted"> (Optional)</span>}
        {editing && unitHint && <span className="text-muted">{unitHint}</span>}
      </Label>
      <Input
        type="text"
        name={field.name}
        className="input-date-filter remove-bottom-margin"
        value={value || ""}
        onChange={onChange}
        placeholder={editing ? field.placeholder : undefined}
        disabled={!editing}
      />
    </FormGroup>
  );
}

function GoalsOfCare({ careProviderId, staffId }) {
  const dispatch = useDispatch();

  const goal = useSelector((state) =>
    staffId
      ? state.careprovider.goalsOfCareStaff
      : careProviderId
      ? state.careprovider.goalsOfCareAdmin
      : state.careprovider.goalsOfCare
  );
  const ongoingRequest = useSelector((state) =>
    staffId
      ? state.careprovider.ongoingGoalsOfCareStaffRequest
      : careProviderId
      ? state.careprovider.ongoingGoalsOfCareAdminRequest
      : state.careprovider.ongoingGoalsOfCareRequest
  );

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(DEFAULT_FIELDS);

  const startEdit = () => {
    setForm(buildDefaultForm(goal));
    setEditing(true);
  };

  const cancelEdit = () => setEditing(false);

  useEffect(() => {
    if (staffId) {
      dispatch({ type: "DO_GET_GOALS_OF_CARE_STAFF", payload: { staffId } });
    } else if (careProviderId) {
      dispatch({ type: "DO_GET_GOALS_OF_CARE_ADMIN", payload: { careProviderId } });
    } else {
      dispatch({ type: "DO_GET_GOALS_OF_CARE" });
    }
  }, [careProviderId, staffId]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (staffId) {
      dispatch({
        type: "DO_SAVE_GOALS_OF_CARE_STAFF",
        payload: { ...form, staffId }
      });
    } else if (careProviderId) {
      dispatch({
        type: "DO_SAVE_GOALS_OF_CARE_ADMIN",
        payload: { ...form, careProviderId }
      });
    } else {
      dispatch({
        type: "DO_SAVE_GOALS_OF_CARE",
        payload: { ...form }
      });
    }
    setEditing(false);
  };

  return (
    <div className="col-12 mx-auto mt-4">
      <div className="header-device-list" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <h3 className="title-device mb-0">
          <i className="mdi mdi-flag-checkered mr-1"></i> Goals of Care
        </h3>
        {!editing && (
          <button type="button" className="btn btn-info print-click-cls" style={{ margin: 0 }} onClick={startEdit}>
            {goal ? (
              <><i className="mdi mdi-pencil" style={{ fontSize: 17 }}></i> Edit Goals</>
            ) : (
              <><i className="mdi mdi-plus" style={{ fontSize: 17 }}></i> Add Goals</>
            )}
          </button>
        )}
      </div>

      {ongoingRequest && (
        <LottieLoader
          loadLoop={true}
          autoplayLoop={true}
          source={lottieLoaderFile}
          Lheight={150}
          Lwidth={150}
          subTitle="Please Wait ..."
        />
      )}

      {!ongoingRequest && !goal && !editing && (
        <LottieLoader
          loadLoop={false}
          autoplayLoop={true}
          source={lottieEmptyFile}
          Lheight={200}
          Lwidth={200}
          titleSmall="No Goals of Care found :("
          subTitleSmall="Click 'Add Goals' to create one."
        />
      )}

      {!ongoingRequest && !editing && goal && (
        <div style={{ backgroundColor: "white", padding: "20px", borderRadius: 6, marginTop: 8 }}>
          <Row>
            {VITAL_GOAL_FIELDS.map((field) => (
              <Col md={6} key={field.name}>
                <GoalField field={field} value={goal[field.name]} editing={false} />
              </Col>
            ))}
          </Row>
          <Row>
            {OTHER_GOAL_FIELDS.map((field) => (
              <Col md={6} key={field.name}>
                <GoalField field={field} value={goal[field.name]} editing={false} />
              </Col>
            ))}
          </Row>
        </div>
      )}

      {!ongoingRequest && editing && (
        <Form onSubmit={handleSubmit}>
          <div style={{ backgroundColor: "white", padding: "20px", borderRadius: 6, marginTop: 8 }}>
            <Row>
              {VITAL_GOAL_FIELDS.map((field) => (
                <Col md={6} key={field.name}>
                  <GoalField
                    field={field}
                    value={form[field.name]}
                    onChange={handleChange}
                    editing={true}
                  />
                </Col>
              ))}
            </Row>
            <Row>
              {OTHER_GOAL_FIELDS.map((field) => (
                <Col md={6} key={field.name}>
                  <GoalField
                    field={field}
                    value={form[field.name]}
                    onChange={handleChange}
                    editing={true}
                  />
                </Col>
              ))}
            </Row>
            <div style={{ display: "flex", justifyContent: "center", gap: "12px", marginTop: 12 }}>
              <button
                type="submit"
                style={{ background: "#4CAF50", color: "#fff", border: "none", borderRadius: "6px", padding: "8px 18px", display: "flex", alignItems: "center", cursor: "pointer", fontSize: "14px" }}
              >
                <i className="mdi mdi-check-circle" style={{ fontSize: 20, marginRight: 8 }}></i>
                {goal ? "Update Goals" : "Save Goals"}
              </button>
              <button
                type="button"
                onClick={cancelEdit}
                style={{ background: "#f44336", color: "#fff", border: "none", borderRadius: "6px", padding: "8px 18px", display: "flex", alignItems: "center", cursor: "pointer", fontSize: "14px" }}
              >
                <i className="mdi mdi-close-circle" style={{ fontSize: 20, marginRight: 8 }}></i>Cancel
              </button>
            </div>
          </div>
        </Form>
      )}
    </div>
  );
}

export default GoalsOfCare;
